import React, { useEffect, useState } from 'react';
import { CreditCard, Tablet, QrCode, MonitorSmartphone, Loader2, WifiOff } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import WalkInSquarePayment from './WalkInSquarePayment';
import WalkInQrPayment from './WalkInQrPayment';
import WalkInTerminalPayment from './WalkInTerminalPayment';

// The payment step. Four equal ways to take the money — the card machine at the
// counter, the card form on this iPad, a code the customer scans with their own
// phone, and a push to the Square Terminal. Whichever staff pick, the sale is
// completed by the same shared pipeline behind the scenes.

const TILE = 'p-6 border-2 border-white/10 hover:border-primary bg-[#111115] text-left transition-colors';

export default function WalkInPayment({ payload, basketKey, onPaid }) {
  const [mode, setMode] = useState(null); // null | 'counter' | 'ipad' | 'qr' | 'terminal'
  const [busy, setBusy] = useState(false);
  const [squareRef, setSquareRef] = useState('');
  const [err, setErr] = useState('');
  const [terminals, setTerminals] = useState([]);
  const [defaultTerminalId, setDefaultTerminalId] = useState('');

  // Rig is chosen up-front on the POS screen.
  const rigType = payload.rigType || '';

  // Which Terminal the sale will go to, and whether it is reachable — so staff
  // can see an offline device before they try to use it rather than after.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await base44.functions.invoke('squareDevices', {});
        if (cancelled || !res.data?.success) return;
        setTerminals(res.data.devices || []);
        setDefaultTerminalId(res.data.defaultDeviceId || '');
      } catch {
        // No device list — the tile shows as unavailable rather than guessing.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const defaultTerminal = terminals.find((d) => d.id === defaultTerminalId) || terminals[0] || null;
  const terminalOffline = !defaultTerminal || defaultTerminal.offline;

  // Counter terminal — staff take the card on the Square machine and link the
  // reference here.
  const confirmMachine = async () => {
    setBusy(true);
    setErr('');
    try {
      const res = await base44.functions.invoke('walkInBooking', {
        ...payload,
        mode: 'machine',
        squarePaymentId: squareRef.trim(),
        rigType,
      });
      if (res.data?.success) {
        onPaid(res.data);
      } else {
        setErr(res.data?.error || 'Could not confirm payment.');
        setBusy(false);
      }
    } catch (e) {
      setErr(e?.message || 'Could not confirm payment.');
      setBusy(false);
    }
  };

  if (mode === 'ipad') {
    return (
      <WalkInSquarePayment
        payload={{ ...payload, rigType }}
        onPaid={onPaid}
        onCancel={() => { setMode(null); setErr(''); }}
      />
    );
  }

  if (mode === 'qr') {
    return (
      <WalkInQrPayment
        payload={payload}
        basketKey={basketKey}
        onPaid={onPaid}
        onCancel={() => { setMode(null); setErr(''); }}
      />
    );
  }

  if (mode === 'terminal') {
    return (
      <WalkInTerminalPayment
        payload={payload}
        basketKey={basketKey}
        devices={terminals}
        onPaid={onPaid}
        onCancel={() => { setMode(null); setErr(''); }}
      />
    );
  }

  if (mode === 'counter') {
    return (
      <div>
        <h2 className="text-white font-black text-3xl uppercase mb-2">Pay at the counter</h2>
        <p className="text-white/40 text-sm mb-6">Please pay on the card machine. A staff member will then link your payment below.</p>

        <label className="block">
          <span className="text-white/40 text-xs font-bold tracking-widest uppercase">Square payment / order reference (staff)</span>
          <input
            value={squareRef}
            onChange={(e) => setSquareRef(e.target.value)}
            placeholder="e.g. ABC123…"
            className="mt-1 w-full bg-[#111115] border border-white/10 focus:border-primary text-white text-lg px-4 py-3.5 outline-none"
          />
        </label>
        {err && <p className="text-red-400 text-sm mt-3">{err}</p>}
        <div className="flex gap-3 mt-6">
          <button onClick={() => { setMode(null); setErr(''); }} disabled={busy} className="border border-white/10 text-white/60 px-6 py-4 font-bold tracking-widest text-xs">BACK</button>
          <button onClick={confirmMachine} disabled={busy || !squareRef.trim()} className="flex-1 bg-primary text-white font-black tracking-widest text-sm py-4 disabled:opacity-40 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} CONFIRM PAYMENT
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-white font-black text-3xl uppercase mb-2">How would you like to pay?</h2>
      <p className="text-white/40 text-sm mb-8">Total <span className="text-primary font-black text-xl">£{payload.amount}</span></p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button onClick={() => { setMode('counter'); setErr(''); }} className={TILE}>
          <CreditCard className="w-8 h-8 text-primary mb-3" />
          <p className="text-white font-black text-lg uppercase">Pay at the counter</p>
          <p className="text-white/40 text-xs mt-1">Staff take your card on the machine</p>
        </button>
        <button onClick={() => { setMode('ipad'); setErr(''); }} className={TILE}>
          <Tablet className="w-8 h-8 text-primary mb-3" />
          <p className="text-white font-black text-lg uppercase">Pay on this iPad</p>
          <p className="text-white/40 text-xs mt-1">Card or Apple Pay on screen</p>
        </button>
        <button onClick={() => { setMode('qr'); setErr(''); }} className={TILE}>
          <QrCode className="w-8 h-8 text-primary mb-3" />
          <p className="text-white font-black text-lg uppercase">Scan to pay</p>
          <p className="text-white/40 text-xs mt-1">Customer pays on their own phone</p>
        </button>
        <button onClick={() => { setMode('terminal'); setErr(''); }} className={TILE}>
          <MonitorSmartphone className="w-8 h-8 text-primary mb-3" />
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-black text-lg uppercase">Pay on terminal</p>
            {terminalOffline && (
              <span className="flex items-center gap-1 border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px] font-bold tracking-widest px-2 py-0.5">
                <WifiOff className="w-3 h-3" /> OFFLINE
              </span>
            )}
          </div>
          <p className="text-white/40 text-xs mt-1">
            {defaultTerminal
              ? `Customer taps on ${defaultTerminal.name}`
              : 'No Square Terminal is set as the default'}
          </p>
        </button>
      </div>
      {err && <p className="text-red-400 text-sm mt-4">{err}</p>}
    </div>
  );
}